import React, { useState } from 'react';
import { Edit, Trash, Loader2 } from 'lucide-react';
import { Workflow } from '@/types/agents';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';

// Mock data - replace with actual API calls
const mockWorkflows: Workflow[] = [
    {
        id: '1',
        name: 'Customer Support Workflow',
        description: 'Automated customer support workflow',
        createdAt: '2024-01-01T10:00:00Z',
        updatedAt: '2024-01-01T10:00:00Z',
    },
    {
        id: '2',
        name: 'Data Processing Pipeline',
        description: 'Processes and transforms data',
        createdAt: '2024-01-01T09:00:00Z',
        updatedAt: '2024-01-01T09:30:00Z',
    },
    {
        id: '3',
        name: 'Report Generation',
        description: 'Generates automated reports',
        createdAt: '2024-01-01T08:00:00Z',
        updatedAt: '2024-01-01T08:00:00Z',
    },
];

interface WorkflowsTableProps {
    onEdit: (workflow: Workflow) => void;
}

const WorkflowsTable: React.FC<WorkflowsTableProps> = ({ onEdit }) => {
    const [workflows] = useState<Workflow[]>(mockWorkflows);
    const [loading] = useState(false);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const handleDelete = async (id: string) => {
        // Handle delete - replace with actual API call
        console.log('Deleting workflow:', id);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 size={32} className="animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div
            className="overflow-x-auto rounded-lg shadow bg-background"
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="text-primary">Name</TableHead>
                        <TableHead className="text-primary">Description</TableHead>
                        <TableHead className="text-primary">Created</TableHead>
                        <TableHead className="text-primary">Updated</TableHead>
                        <TableHead className="text-primary text-right">
                            Actions
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {workflows.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={5} className="text-center py-8">
                                No workflows have been created yet. Use the "Create Workflow" button to create one.
                            </TableCell>
                        </TableRow>
                    ) : (
                        workflows.map((workflow) => (
                            <TableRow
                                key={workflow.id}
                                className="hover:bg-muted/10 transition-colors"
                            >
                                <TableCell className="text-foreground">
                                    <div className="font-medium">{workflow.name}</div>
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    {workflow.description || 'No description'}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    {formatDate(workflow.createdAt)}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    {formatDate(workflow.updatedAt)}
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex justify-end gap-2">
                                        <button
                                            onClick={() => onEdit(workflow)}
                                            className="transition-colors text-primary hover:text-secondary"
                                        >
                                            <Edit size={18} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(workflow.id)}
                                            className="transition-colors text-destructive hover:opacity-70"
                                        >
                                            <Trash size={18} />
                                        </button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </div>
    );
};

export default WorkflowsTable;

